import {
  CustomDlpFilterType,
  DlpFilter,
  DlpFilterPolicy,
  Provider,
  PROVIDER_DISPLAY_NAMES
} from '@kindo/universal';

import { DlpFiltersConfig } from '~/components/SettingsModal/SecuritySettings/OrgSecurityControls/DlpFilters/DlpFiltersConfig';
import { transformDlpFiltersConfigResponse } from '~/components/SettingsModal/SecuritySettings/OrgSecurityControls/DlpFilters/DlpFiltersConfig/DlpFilters.types';
import { useMutateDlpConfigs } from '~/hooks/useMutateDlpConfigs';
import { nextTrpc } from '~/trpc';

interface UserGroupProvidersDlpFiltersProps {
  canAccessSecuritySettings: boolean;
  provider: Provider;
  userGroupId: string;
}
const UserGroupProvidersDlpFiltersConfig: React.FC<
  UserGroupProvidersDlpFiltersProps
> = ({ canAccessSecuritySettings, provider, userGroupId }) => {
  // Queries
  const { data, refetch: refetchCustomFilters } =
    nextTrpc.customDlpFilters.listUserGroupProviderCustomDlpFilters.useQuery({
      provider,
      userGroupId
    });

  const customDlpFilters = data?.items ?? [];

  const {
    data: dlpFiltersConfig,
    refetch: refetchDlpFiltersConfig,
    isLoading
  } = nextTrpc.dlpFiltersConfig.getUserGroupProviderDlpFiltersConfig.useQuery(
    { userGroupId, provider },
    {
      select: transformDlpFiltersConfigResponse
    }
  );

  // Custom Hooks
  const {
    updatingFilters,
    updateDlpFiltersConfig,
    createCustomDlpFilter,
    deleteCustomDlpFilter
  } = useMutateDlpConfigs({
    model: null,
    provider,
    userGroupId,
    onSettled: () => {
      refetchDlpFiltersConfig();
      refetchCustomFilters();
    }
  });

  const handleDlpFilterOptionToggle = (
    dlpFilter: DlpFilter,
    newPolicy: Extract<
      DlpFilterPolicy,
      DlpFilterPolicy.REDACT | DlpFilterPolicy.PASSTHROUGH
    >
  ) => {
    updateDlpFiltersConfig({
      filter: dlpFilter,
      policy: newPolicy
    });
  };

  const handleCreateCustomDlpFilter = (
    policy: DlpFilterPolicy,
    type: CustomDlpFilterType,
    value: string
  ) => {
    createCustomDlpFilter({
      policy,
      type,
      value
    });
  };

  const handleDeleteCustomDlpFilter = (value: string) => {
    deleteCustomDlpFilter({
      value
    });
  };

  return (
    <DlpFiltersConfig
      canAccessSecuritySettings={canAccessSecuritySettings}
      createCustomDlpFilter={handleCreateCustomDlpFilter}
      customDlpFilters={customDlpFilters}
      deleteCustomDlpFilter={handleDeleteCustomDlpFilter}
      dlpFiltersConfig={dlpFiltersConfig}
      dlpFiltersConfigIsLoading={isLoading}
      targetResourceLabel={`${PROVIDER_DISPLAY_NAMES[provider]}'s models that have DLP enabled`}
      updateStandardDlpFilterConfig={handleDlpFilterOptionToggle}
      updatingFilters={updatingFilters}
    />
  );
};
export default UserGroupProvidersDlpFiltersConfig;
